import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  error: string | null;
  loading: boolean;
}

export const useGeolocation = () => {
  const { user, refreshProfile } = useAuthContext();
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    error: null,
    loading: false
  });

  // Request and save user's location
  const requestLocation = useCallback(async () => {
    if (!user) return { success: false, error: "Not authenticated" };

    if (!("geolocation" in navigator)) {
      setState(prev => ({ ...prev, error: "Geolocation not supported" }));
      return { success: false, error: "Geolocation not supported" };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    return new Promise<{ success: boolean; error?: string }>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Save to database
          const { error: dbError } = await supabase
            .from("profiles")
            .update({ latitude, longitude })
            .eq("id", user.id);

          if (dbError) {
            setState(prev => ({ 
              ...prev, 
              loading: false, 
              error: "Failed to save location" 
            }));
            resolve({ success: false, error: "Failed to save location" });
            return;
          }

          setState({
            latitude,
            longitude,
            error: null,
            loading: false
          });

          refreshProfile();
          resolve({ success: true });
        },
        (geoError) => {
          let errorMessage = "Failed to get location";
          switch (geoError.code) {
            case geoError.PERMISSION_DENIED:
              errorMessage = "Location permission denied";
              break;
            case geoError.POSITION_UNAVAILABLE:
              errorMessage = "Location unavailable";
              break;
            case geoError.TIMEOUT:
              errorMessage = "Location request timed out";
              break;
          }

          setState(prev => ({ 
            ...prev, 
            loading: false, 
            error: errorMessage 
          }));
          resolve({ success: false, error: errorMessage });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }, [user, refreshProfile]);

  // Calculate distance between current user and another user
  const calculateDistance = useCallback((
    targetLat: number | null,
    targetLng: number | null,
    userLat: number | null,
    userLng: number | null
  ): string => {
    if (!targetLat || !targetLng || !userLat || !userLng) {
      return "Distance unavailable";
    }

    // Haversine formula for accurate distance
    const R = 3959; // Earth's radius in miles
    const lat1 = (userLat * Math.PI) / 180;
    const lat2 = (targetLat * Math.PI) / 180;
    const dLat = toRad(targetLat - userLat);
    const dLng = toRad(targetLng - userLng);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * 
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // Always show one decimal place
    return `${distance.toFixed(1)} miles away`;
  }, []);

  return {
    ...state,
    requestLocation,
    calculateDistance
  };
};

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Hook for fetching profiles with distance
export const useProfilesWithDistance = () => {
  const { user, profile } = useAuthContext();
  const { calculateDistance } = useGeolocation();
  const [maxDistance, setMaxDistance] = useState(50); // Default 50 miles

  const getProfilesWithDistance = useCallback(async () => {
    if (!user) return [];

    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .neq("id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error || !data) return [];

    const userLat = profile?.latitude ?? null;
    const userLng = profile?.longitude ?? null;

    // Add distance to each profile and filter
    const profilesWithDistance = data.map(p => {
      const distance = calculateDistance(
        p.latitude,
        p.longitude,
        userLat,
        userLng
      );
      
      // Parse distance for filtering
      let distanceNum = Infinity;
      if (distance !== "Unknown") {
        const match = distance.match(/(\d+)/);
        if (match) {
          distanceNum = parseInt(match[1]);
        } else if (distance === "< 1 mile") {
          distanceNum = 0.5;
        }
      }

      return {
        ...p,
        distanceText: distance,
        distanceNum
      };
    });

    // Filter by max distance
    return profilesWithDistance.filter(p => p.distanceNum <= maxDistance);
  }, [user, profile, calculateDistance, maxDistance]);

  return {
    maxDistance,
    setMaxDistance,
    getProfilesWithDistance
  };
};
